/**
 * ============================================================================
 * USE SECURITY HOOK
 * ============================================================================
 * 
 * Custom hook for security monitoring and management
 */

import { useState, useEffect, useCallback } from 'react';
import * as securityService from '../services/security';

export const useSecurity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [suspiciousActivity, setSuspiciousActivity] = useState(null);
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [securitySettings, setSecuritySettings] = useState(null);
  const [statistics, setStatistics] = useState(null);

  /**
   * Load security data on mount
   */
  useEffect(() => {
    loadSecurityEvents();
    loadLoginHistory();
    loadActiveSessions();
    loadSecurityAlerts();
    loadIpWhitelist();
  }, []);

  /**
   * Get security events (graceful on 404 / missing backend)
   */
  const loadSecurityEvents = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await securityService.getSecurityEvents(filters);
      const events = response?.data?.events ?? response?.events ?? [];
      setSecurityEvents(Array.isArray(events) ? events : []);
      return response?.data ?? response;
    } catch (err) {
      setSecurityEvents([]);
      setError(err?.message || 'Failed to load security events');
      return { events: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get login history (graceful on 404 / missing backend)
   */
  const loadLoginHistory = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await securityService.getLoginHistory(filters);
      const history = response?.data?.history ?? response?.history ?? [];
      setLoginHistory(Array.isArray(history) ? history : []);
      return response?.data ?? response;
    } catch (err) {
      setLoginHistory([]);
      setError(err?.message ?? err?.error?.message ?? 'Failed to load login history');
      return { history: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get active sessions (graceful on 404 / missing backend)
   */
  const loadActiveSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const sessions = await securityService.getActiveSessions();
      const list = Array.isArray(sessions) ? sessions : [];
      setActiveSessions(list);
      return list;
    } catch (err) {
      setActiveSessions([]);
      setError(err?.message || 'Failed to load active sessions');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Revoke session
   */
  const revokeSession = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      setError(null);
      await securityService.revokeSession(sessionId);
      await loadActiveSessions(); // Refresh list
    } catch (err) {
      setError(err.message || 'Failed to revoke session');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadActiveSessions]);

  /**
   * Revoke all other sessions
   */
  const revokeAllOtherSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await securityService.revokeAllOtherSessions();
      await loadActiveSessions(); // Refresh list
    } catch (err) {
      setError(err.message || 'Failed to revoke all other sessions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadActiveSessions]);

  /**
   * Get security alerts (graceful on 404 / missing backend)
   */
  const loadSecurityAlerts = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await securityService.getSecurityAlerts(filters);
      const alerts = response?.data?.alerts ?? response?.alerts ?? [];
      setSecurityAlerts(Array.isArray(alerts) ? alerts : []);
      return response?.data ?? response;
    } catch (err) {
      setSecurityAlerts([]);
      setError(err?.message || 'Failed to load security alerts');
      return { alerts: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Mark alert as read
   */
  const markAlertAsRead = useCallback(async (alertId) => {
    try {
      setLoading(true);
      setError(null);
      await securityService.markAlertAsRead(alertId);
      await loadSecurityAlerts(); // Refresh alerts
    } catch (err) {
      setError(err.message || 'Failed to mark alert as read');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadSecurityAlerts]);

  /**
   * Get suspicious activity
   */
  const loadSuspiciousActivity = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await securityService.getSuspiciousActivity(filters);
      setSuspiciousActivity(response.data || response);
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to load suspicious activity');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Report suspicious activity
   */
  const reportSuspiciousActivity = useCallback(async (report) => {
    try {
      setLoading(true);
      setError(null);
      await securityService.reportSuspiciousActivity(report);
      await loadSecurityEvents(); // Refresh events
    } catch (err) {
      setError(err.message || 'Failed to report suspicious activity');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadSecurityEvents]);

  /**
   * Get IP whitelist (graceful on 404 / missing backend)
   */
  const loadIpWhitelist = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const ips = await securityService.getIPWhitelist();
      const list = Array.isArray(ips) ? ips : [];
      setIpWhitelist(list);
      return list;
    } catch (err) {
      setIpWhitelist([]);
      setError(err?.message || 'Failed to load IP whitelist');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Add IP to whitelist
   */
  const addIpToWhitelist = useCallback(async (ip) => {
    try {
      setLoading(true);
      setError(null);
      await securityService.addIPToWhitelist(ip);
      await loadIpWhitelist(); // Refresh list
    } catch (err) {
      setError(err.message || 'Failed to add IP to whitelist');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadIpWhitelist]);

  /**
   * Remove IP from whitelist
   */
  const removeIpFromWhitelist = useCallback(async (ip) => {
    try {
      setLoading(true);
      setError(null);
      await securityService.removeIPFromWhitelist(ip);
      await loadIpWhitelist(); // Refresh list
    } catch (err) {
      setError(err.message || 'Failed to remove IP from whitelist');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadIpWhitelist]);

  /**
   * Get security settings (graceful on 404 / missing backend)
   */
  const loadSecuritySettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const settings = await securityService.getSecuritySettings();
      setSecuritySettings(settings ?? null);
      return settings ?? null;
    } catch (err) {
      setSecuritySettings(null);
      setError(err?.message || 'Failed to load security settings');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update security settings
   */
  const updateSecuritySettings = useCallback(async (settings) => {
    try {
      setLoading(true);
      setError(null);
      const updatedSettings = await securityService.updateSecuritySettings(settings);
      setSecuritySettings(updatedSettings);
      return updatedSettings;
    } catch (err) {
      setError(err.message || 'Failed to update security settings');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Enable login alerts
   */
  const enableLoginAlerts = useCallback(async (options) => {
    try {
      setLoading(true);
      setError(null);
      await securityService.enableLoginAlerts(options);
      await loadSecuritySettings(); // Refresh settings
    } catch (err) {
      setError(err.message || 'Failed to enable login alerts');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadSecuritySettings]);

  /**
   * Disable login alerts
   */
  const disableLoginAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await securityService.disableLoginAlerts();
      await loadSecuritySettings(); // Refresh settings
    } catch (err) {
      setError(err.message || 'Failed to disable login alerts');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadSecuritySettings]);

  /**
   * Get security statistics
   */
  const loadStatistics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await securityService.getSecurityStatistics(filters);
      setStatistics(response.data || response);
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to load security statistics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    securityEvents,
    loginHistory,
    activeSessions,
    securityAlerts,
    suspiciousActivity,
    ipWhitelist,
    securitySettings,
    statistics,
    loading,
    error,
    
    // Actions
    loadSecurityEvents,
    loadLoginHistory,
    loadActiveSessions,
    revokeSession,
    revokeAllOtherSessions,
    loadSecurityAlerts,
    markAlertAsRead,
    loadSuspiciousActivity,
    reportSuspiciousActivity,
    loadIpWhitelist,
    addIpToWhitelist,
    removeIpFromWhitelist,
    loadSecuritySettings,
    updateSecuritySettings,
    enableLoginAlerts,
    disableLoginAlerts,
    loadStatistics,
    
    // Convenience
    refresh: () => {
      loadSecurityEvents();
      loadLoginHistory();
      loadActiveSessions();
      loadSecurityAlerts();
      loadIpWhitelist();
    },
  };
};

export default useSecurity;
